import {
  FileExtensionReference,
  FileStatus,
  ServicesFileStatusResponseData,
} from '@kaa/api/providers';
import { httpTo } from '@kaa/api/providers/utilities';
import { useAsyncCallback } from '@kaa/common/utils';
import { i18nKeys } from '@kaa/i18n/providers/keys';
import {
  AlertType,
  getAlertPropsByType,
  SwAlert,
  SwDocument,
  SwLoader,
  SwTitle,
} from '@kaa/ui-flanders/components';
import React, { PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadReport } from '../../../../components';
import { Modals } from '../../../../constants';
import { useApi } from '../../../../utils';
import { sendConfirmationModalViewEvent } from '../../../../utils/google-analytics';
import { getApplicationType } from '../../../../utils/templates';

type PrestationImportModalConfirmationProps = {
  providerId: number;
  fileStatus: ServicesFileStatusResponseData;
};

export const PrestationImportModalConfirmation = ({
  children,
  providerId,
  fileStatus,
}: PropsWithChildren<PrestationImportModalConfirmationProps>) => {
  const { t } = useTranslation();

  useEffect(() => {
    sendConfirmationModalViewEvent(
      Modals.PRESTATIONS_IMPORT_LIST_PRESTATION_MODAL,
    );
  }, []);

  const { providers: providersApi } = useApi();

  const [
    { value: reportFile, loading: reportLoading },
    getReport,
  ] = useAsyncCallback(
    async (fileExtension: FileExtensionReference) => {
      const [, payload] = await httpTo(
        providersApi.exportFileServices(providerId, {
          fileExtension,
        }),
      );

      if (!payload) {
        return undefined;
      }

      const blob = new Blob([payload.data as BlobPart], {
        type: getApplicationType(fileExtension || FileExtensionReference.XLSX),
      });

      const url = window.URL.createObjectURL(blob);

      return { url, blob };
    },
    [providersApi],
  );

  const { status, totalRows, created, invalid } = fileStatus;

  const {
    created: keyCreated,
    refused: keyInvalid,
  } = i18nKeys.prestations.modals.import.confirmation.alert;

  const downloadExtensions = [
    FileExtensionReference.XLSX,
    FileExtensionReference.CSV,
  ];

  return (
    <>
      {children}
      {status === FileStatus.Done && (
        <SwAlert
          {...getAlertPropsByType(AlertType.SUCCESS)}
          className="vl-u-spacer"
          title={t(
            i18nKeys.prestations.modals.import.confirmation.alert.success.title,
          )}
        >
          <p>{`${created}/${totalRows}`}</p>
        </SwAlert>
      )}
      {status === FileStatus.DoneWithError && (
        <SwAlert
          {...getAlertPropsByType(AlertType.ERROR)}
          className="vl-u-spacer"
          title={t(
            i18nKeys.prestations.modals.import.confirmation.alert.error.title,
          )}
        >
          <p>
            {created > 0 && (
              <>
                {`${t(keyCreated)} ${created}/${totalRows}`}
                <br />
              </>
            )}
            {invalid > 0 && (
              <>
                {`${t(keyInvalid)} ${invalid}/${totalRows}`}
                <br />
              </>
            )}
            <br />
            {t(
              i18nKeys.prestations.modals.import.confirmation.alert.error
                .messageBottom,
            )}
          </p>
        </SwAlert>
      )}
      {reportLoading ? (
        <SwLoader modMessageHidden />
      ) : (
        <>
          {reportFile ? (
            <>
              <SwTitle tagName="h5" className="vl-u-spacer--small">
                {t(
                  i18nKeys.prestations.modals.import.confirmation.downloadReport
                    .title,
                )}
              </SwTitle>
              <DownloadReport
                modalId={Modals.PRESTATIONS_IMPORT_LIST_PRESTATION_MODAL}
                reportFile={reportFile.blob}
                reportUrl={reportFile.url}
                reportName={t(
                  i18nKeys.prestations.modals.import.confirmation.downloadReport
                    .fileName,
                )}
                reportLabel={t(
                  i18nKeys.prestations.modals.import.confirmation.downloadReport
                    .linkTitle,
                )}
              />
            </>
          ) : (
            <>
              <SwTitle tagName="h5" className="vl-u-spacer--small">
                {t(
                  i18nKeys.prestations.modals.import.confirmation.downloadReport
                    .generate,
                )}
              </SwTitle>
              <div className="vl-u-display-flex">
                {downloadExtensions.map(
                  (fileExtension: FileExtensionReference, i) => (
                    <SwDocument
                      key={fileExtension}
                      style={i > 0 ? { marginLeft: '2rem' } : undefined}
                      mimeType={fileExtension}
                      title={t(i18nKeys.general.label.format, {
                        format: fileExtension.toLowerCase(),
                      })}
                      onClick={(e) => {
                        e.preventDefault();
                        getReport(fileExtension);
                      }}
                    />
                  ),
                )}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};
